import React from 'react';
import { motion } from 'framer-motion';
import { FaEye, FaRedo, FaHeart } from 'react-icons/fa';
import { Order, OrderStatusLabels, OrderStatusColors } from '../../types/order';
import { formatOrderDateTime, formatPrice, getStatusIconElement } from '../../utils/orderUtils';

interface UserOrderCardProps {
  order: Order;
  index: number;
  favorites: Set<number>;
  onOrderDetailClick: (order: Order) => void;
  onReorderClick: (order: Order) => void;
  onFavoriteToggle: (orderId: number) => void;
}

const UserOrderCard: React.FC<UserOrderCardProps> = ({
  order,
  index,
  favorites,
  onOrderDetailClick,
  onReorderClick,
  onFavoriteToggle,
}) => {
  return (
    <motion.div
      key={order.id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.05, 0.3) }}
      className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/50 hover:shadow-xl transition-shadow duration-200"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl">
            {getStatusIconElement(order.status)}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-lg font-bold text-gray-800">Sipariş #{order.id}</h3>
              <button
                onClick={() => onFavoriteToggle(order.id)}
                className={`group relative p-2 rounded-lg transition-colors duration-150 ${
                  favorites.has(order.id)
                    ? 'bg-red-50 text-red-500'
                    : 'bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500'
                }`}
              >
                <FaHeart
                  className={`transition-colors duration-150 ${
                    favorites.has(order.id) ? 'text-red-500' : ''
                  }`}
                />
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {favorites.has(order.id) ? 'Favorilerden çıkar' : 'Favorilere ekle'}
                </div>
              </button>
            </div>
            <p className="text-gray-600 font-medium">{formatOrderDateTime(order.created_at)}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-800 mb-2">
            {formatPrice(order.total_amount || 0)}
          </div>
          <span
            className={`text-sm px-4 py-2 rounded-full font-medium ${OrderStatusColors[order.status]}`}
          >
            {OrderStatusLabels[order.status]}
          </span>
        </div>
      </div>

      {/* Sipariş Detayları */}
      <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Birim/Ofis:</span>
              <span className="text-sm text-gray-800 font-medium">
                {order.management} - {order.office}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Kullanılan Fiş:</span>
              <span className="text-sm text-green-600 font-bold">
                {formatPrice(order.ticket_used || 0)}
              </span>
            </div>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 block mb-1">Ürünler:</span>
            <p className="text-sm text-gray-800 line-clamp-2">
              {order.items_summary || 'Detay yok'}
            </p>
          </div>
        </div>

        {/* Dynamic Options Gösterimi */}
        {order.items && order.items.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <span className="text-sm font-medium text-gray-500 block mb-2">Seçenekler:</span>
            <div className="space-y-2">
              {order.items.slice(0, 3).map((item, index) => (
                <div key={index} className="text-sm">
                  <span className="font-medium text-gray-700">{item.name}:</span>
                  {item.options && Object.keys(item.options).length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Object.entries(item.options).map(([key, value]) => {
                        // Türkçe label mapping
                        const labelMap: Record<string, string> = {
                          boyut: 'Boyut',
                          dem: 'Dem',
                          seker: 'Şeker',
                          sicaklik: 'Sıcaklık',
                        };

                        // Türkçe value mapping
                        const valueMap: Record<string, string> = {
                          normal: 'Normal',
                          double: 'Büyük',
                          acik: 'Açık',
                          demli: 'Demli',
                          sekersiz: 'Şekersiz',
                          orta: 'Orta',
                          sekerli: 'Şekerli',
                          sicak: 'Sıcak',
                          ilik: 'Ilık',
                          soguk: 'Soğuk',
                        };

                        const displayLabel = labelMap[key] || key;
                        const displayValue = valueMap[value as string] || String(value);

                        return (
                          <span
                            key={key}
                            className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"
                          >
                            {displayLabel}: {displayValue}
                          </span>
                        );
                      })}
                    </div>
                  ) : item.size || item.strength ? (
                    // Backward Compatibility - Eski Format
                    <div className="flex gap-1 mt-1">
                      {item.size && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          Boyut: {item.size === 'normal' ? 'Normal' : 'Büyük'}
                        </span>
                      )}
                      {item.strength && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                          Dem:{' '}
                          {item.strength === 'light'
                            ? 'Açık'
                            : item.strength === 'normal'
                              ? 'Normal'
                              : 'Demli'}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="ml-2 text-gray-400 text-xs">Seçenek yok</span>
                  )}
                </div>
              ))}
              {order.items.length > 3 && (
                <div className="text-xs text-gray-400 italic">
                  +{order.items.length - 3} ürün daha... (Detayları görüntülemek için "Detay"
                  butonuna tıklayın)
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Aksiyon Butonları */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400">#{order.id}</div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onOrderDetailClick(order)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium"
          >
            <FaEye />
            Detay
          </button>
          {order.status === 'completed' && (
            <button
              onClick={() => onReorderClick(order)}
              className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors font-medium"
            >
              <FaRedo />
              Tekrar Sipariş Ver
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default UserOrderCard;
